import React from 'react';
import { WeeklyPlan, Task, PhysicalUnit, Level, Responsible, CNCGroup, ProtocolState, ProtocolConfig, PresentationWidget, ProjectInfo } from '../../types';
import { generateDynamicDayColumns, DynamicDayColumn } from '../../../pages/Planning';
import { cleanTaskName, findTaskName, isProtocolProgressed } from '../../utils/planningUtils';

export interface WeeklyPlanDataProps {
    weeklyPlans: WeeklyPlan[];
    tasks: Task[];
    units: PhysicalUnit[];
    levels: Level[];
    responsibles: Responsible[];
    cncGroups: CNCGroup[];
    savedProtocols: Record<string, ProtocolState> | ProtocolState[];
    protocolConfigs: Record<string, ProtocolConfig>;
    activePlanId?: number | null;
    matrixData?: Record<string, any>;
    projectInfo?: ProjectInfo;
    subcontractors?: any[];
    contracts?: any[];
    baselines?: any[];
    progressHistory?: any[];
    evaluationConfig?: any;
    lookaheadPlans?: any[];
    globalUsers?: any[];
}

interface WeeklyPlanTableWidgetProps {
    widget: PresentationWidget;
    data?: WeeklyPlanDataProps;
    isSelected?: boolean;
    onSplitIntoSlides?: () => void;
}

export const WeeklyPlanTableWidget: React.FC<WeeklyPlanTableWidgetProps> = ({
    widget,
    data,
    isSelected,
    onSplitIntoSlides,
}) => {
    // Determine the previous plan
    const {
        weeklyPlans = [],
        tasks = [],
        units = [],
        levels = [],
        responsibles = [],
        cncGroups = [],
        savedProtocols: rawSavedProtocols = {},
        protocolConfigs = {},
        activePlanId,
    } = data || {};

    const savedProtocols = React.useMemo<Record<string, ProtocolState>>(() => {
        if (!rawSavedProtocols) return {};
        if (Array.isArray(rawSavedProtocols)) {
            const map: Record<string, ProtocolState> = {};
            rawSavedProtocols.forEach(p => { if (p?.id) map[p.id] = p; });
            return map;
        }
        return rawSavedProtocols as Record<string, ProtocolState>;
    }, [rawSavedProtocols]);

    const isCurrentPlanWidget = widget.type === 'weekly_current';

    // Target plan: for weekly_current it is the latest/active plan, otherwise previous plan
    const targetPlan = React.useMemo(() => {
        if (!weeklyPlans || weeklyPlans.length === 0) return null;
        const sorted = [...weeklyPlans].sort((a, b) => a.id - b.id);

        if (isCurrentPlanWidget) {
            // Pick activePlanId if provided, or the plan not closed, or the very last plan
            if (activePlanId) {
                const found = sorted.find(p => p.id === activePlanId);
                if (found) return found;
            }
            return sorted.find(p => p.status !== 'Cerrada') || sorted[sorted.length - 1];
        }

        // For previous plan widgets:
        if (activePlanId) {
            const currentIdx = sorted.findIndex(p => p.id === activePlanId);
            if (currentIdx > 0) {
                return sorted[currentIdx - 1];
            }
        }
        // Fallback: the plan before the last, or the last closed/last plan
        if (sorted.length >= 2) {
            return sorted[sorted.length - 2];
        }
        return sorted[0];
    }, [weeklyPlans, activePlanId, isCurrentPlanWidget]);

    // Dynamic columns for days
    const dayColumns = React.useMemo(() => {
        return generateDynamicDayColumns(targetPlan?.startDate, targetPlan?.endDate);
    }, [targetPlan?.startDate, targetPlan?.endDate]);

    // Helper to get status value
    const getStatusVal = (alloc: any): 'si' | 'no' | 'pendiente' => {
        if (alloc.executed === true) return 'si';
        if (alloc.executed === false) return 'no';
        return 'pendiente';
    };

    // Filter allocations according to widget type
    const filteredAllocations = React.useMemo(() => {
        if (!targetPlan || !targetPlan.allocations) return [];
        return targetPlan.allocations.filter(alloc => {
            const status = getStatusVal(alloc);
            if (widget.type === 'weekly_prev_executed') {
                return status === 'si';
            }
            if (widget.type === 'weekly_prev_not_executed') {
                return status === 'no';
            }
            return true; // weekly_prev_full or weekly_current
        });
    }, [targetPlan, widget.type]);

    // Pagination slice if configured
    const pageSize = widget.config?.pageSize || 12;
    const pageIndex = widget.config?.pageIndex || 0;
    const totalPages = Math.max(1, Math.ceil(filteredAllocations.length / pageSize));

    const displayedAllocations = React.useMemo(() => {
        if (widget.config?.usePagination === false) {
            return filteredAllocations;
        }
        const start = pageIndex * pageSize;
        return filteredAllocations.slice(start, start + pageSize);
    }, [filteredAllocations, pageIndex, pageSize, widget.config?.usePagination]);

    // Column visibility based on type: weekly_current and weekly_prev_executed hide CNC & justification
    const showCNCAndJustification = widget.type !== 'weekly_prev_executed' && widget.type !== 'weekly_current';

    if (!targetPlan) {
        return (
            <div className="w-full h-full flex flex-col items-center justify-center bg-white dark:bg-slate-900 text-slate-400 p-4 border border-dashed border-slate-300 rounded-lg">
                <span className="material-symbols-outlined text-[32px] mb-1 text-slate-400">history_toggle_off</span>
                <span className="text-xs font-semibold">
                    {isCurrentPlanWidget ? 'No se encontró una planificación semanal actual' : 'No se encontró una planificación semanal anterior'}
                </span>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col bg-white overflow-hidden select-none text-[10px] leading-tight font-sans">
            {/* Top Banner (Indigo / Purple #312e81) */}
            <div
                style={{ backgroundColor: '#312e81' }}
                className="text-white px-3 py-1.5 font-bold text-center tracking-wide flex items-center justify-between shrink-0 shadow-sm"
            >
                <div className="flex-1 text-center truncate text-[11px]">
                    PLANIFICACIÓN SEMANAL #{targetPlan.id} ({targetPlan.startDate} al {targetPlan.endDate}) - ESTADO: {targetPlan.status.toUpperCase()}
                    {totalPages > 1 && (
                        <span className="ml-2 text-indigo-200 font-normal text-[10px]">
                            (Parte {pageIndex + 1} de {totalPages})
                        </span>
                    )}
                </div>
                {totalPages > 1 && onSplitIntoSlides && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onSplitIntoSlides();
                        }}
                        title="Crear diapositivas para las partes restantes"
                        className="bg-white/20 hover:bg-white/30 text-white text-[9px] font-semibold px-2 py-0.5 rounded transition flex items-center gap-1 shrink-0 ml-2"
                    >
                        <span className="material-symbols-outlined text-[12px]">auto_stories</span>
                        Dividir
                    </button>
                )}
            </div>

            {/* Table Container */}
            <div className="flex-1 overflow-auto">
                <table className="w-full border-collapse text-left">
                    <thead>
                        <tr style={{ backgroundColor: '#1e293b' }} className="text-white font-bold text-[9px]">
                            <th className="p-1 border border-slate-600 text-center whitespace-nowrap">Nivel</th>
                            <th className="p-1 border border-slate-600 text-center whitespace-nowrap">Unidad Física (Sitio)</th>
                            <th className="p-1 border border-slate-600 text-left min-w-[120px]">Tarea / Partida</th>
                            <th className="p-1 border border-slate-600 text-left min-w-[90px]">Responsable</th>
                            {dayColumns.map(col => (
                                <th key={col.key} className="p-1 border border-slate-600 text-center w-6 whitespace-nowrap">
                                    {col.dayLetter} ({col.dateNum})
                                </th>
                            ))}
                            <th className="p-1 border border-slate-600 text-center whitespace-nowrap">% Comp.</th>
                            <th className="p-1 border border-slate-600 text-center whitespace-nowrap">% Cump.</th>
                            <th className="p-1 border border-slate-600 text-center whitespace-nowrap">Estado</th>
                            {showCNCAndJustification && (
                                <>
                                    <th className="p-1 border border-slate-600 text-left min-w-[110px]">C.N.C (Causa No Cumplimiento)</th>
                                    <th className="p-1 border border-slate-600 text-left min-w-[110px]">Justificación de Incumplimiento</th>
                                </>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {displayedAllocations.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={4 + dayColumns.length + 3 + (showCNCAndJustification ? 2 : 0)}
                                    className="p-6 text-center text-slate-400 italic text-xs"
                                >
                                    No hay tareas para el criterio seleccionado en esta semana.
                                </td>
                            </tr>
                        ) : (
                            displayedAllocations.map((alloc, idx) => {
                                const unit = units.find(u => u.id === alloc.unitId);
                                const unitName = unit?.name || 'N/A';
                                const levelName = levels.find(l => l.id === unit?.levelId)?.name || '-';
                                const rawTaskName = alloc.isAdditional
                                    ? (alloc.additionalTaskName || 'Tarea Adicional')
                                    : (findTaskName(alloc.taskId, tasks) || '[Partida Obsoleta]');
                                const taskName = cleanTaskName(rawTaskName);
                                const responsibleName = responsibles.find(r => r.id === alloc.responsibleId)?.name || '-';

                                const config = protocolConfigs[alloc.taskId];
                                const appliesProtocol = config?.applies ?? false;
                                const isExecuted = appliesProtocol
                                    ? isProtocolProgressed(alloc.taskId, alloc.unitId, savedProtocols, protocolConfigs)
                                    : alloc.executed;

                                const pctComprometido = alloc.percentComprometido ?? 100;
                                const pctCumplimiento = alloc.percentCumplimiento ?? (isExecuted ? 100 : 0);
                                const statusVal = getStatusVal(alloc);

                                const cncName = alloc.cncId
                                    ? (cncGroups.flatMap(g => g.causes).find(c => c.id === alloc.cncId)?.name || alloc.cncId)
                                    : '-';
                                const justificationText = alloc.comentario?.trim() || '-';

                                const isEven = idx % 2 === 0;
                                const rowBg = isEven ? 'bg-white' : 'bg-slate-50';

                                return (
                                    <tr key={`${alloc.taskId}_${alloc.unitId}_${idx}`} className={`${rowBg} border-b border-slate-200 hover:bg-blue-50/50`}>
                                        <td className="p-1 border border-slate-200 text-center text-slate-700 whitespace-nowrap">{levelName}</td>
                                        <td className="p-1 border border-slate-200 text-center text-slate-700 whitespace-nowrap">{unitName}</td>
                                        <td className={`p-1 border border-slate-200 ${alloc.isAdditional ? 'bg-purple-50 text-purple-800 font-semibold' : 'text-slate-800'}`}>
                                            {taskName}
                                        </td>
                                        <td className="p-1 border border-slate-200 text-slate-600 truncate max-w-[110px]">{responsibleName}</td>
                                        
                                        {/* Days */}
                                        {dayColumns.map(col => {
                                            const isChecked = !!alloc.days?.[col.dayLetter];
                                            return (
                                                <td
                                                    key={col.key}
                                                    style={{ backgroundColor: isChecked ? '#dcfce7' : undefined }}
                                                    className={`p-1 border border-slate-200 text-center font-bold ${isChecked ? 'text-green-800' : 'text-slate-300'}`}
                                                >
                                                    {isChecked ? 'X' : ''}
                                                </td>
                                            );
                                        })}

                                        {/* % Comprometido */}
                                        <td className="p-1 border border-slate-200 text-center text-slate-700 font-medium">
                                            {pctComprometido}%
                                        </td>

                                        {/* % Cumplimiento */}
                                        <td
                                            style={{
                                                backgroundColor: pctCumplimiento >= 100 ? '#dcfce7' : pctCumplimiento > 0 ? '#fef3c7' : targetPlan.status === 'Cerrada' || statusVal === 'no' ? '#fee2e2' : undefined,
                                                color: pctCumplimiento >= 100 ? '#15803d' : pctCumplimiento > 0 ? '#b45309' : targetPlan.status === 'Cerrada' || statusVal === 'no' ? '#b91c1c' : '#334155'
                                            }}
                                            className="p-1 border border-slate-200 text-center font-bold"
                                        >
                                            {pctCumplimiento}%
                                        </td>

                                        {/* Estado */}
                                        <td
                                            style={{
                                                backgroundColor: statusVal === 'si' ? '#dcfce7' : statusVal === 'no' ? '#fee2e2' : '#fef9c3',
                                                color: statusVal === 'si' ? '#15803d' : statusVal === 'no' ? '#b91c1c' : '#a16207'
                                            }}
                                            className="p-1 border border-slate-200 text-center font-semibold whitespace-nowrap"
                                        >
                                            {statusVal === 'si' ? 'Ejecutado' : statusVal === 'no' ? 'No Ejecutado' : 'Pendiente'}
                                        </td>

                                        {/* C.N.C & Justification */}
                                        {showCNCAndJustification && (
                                            <>
                                                <td className={`p-1 border border-slate-200 text-slate-700 ${statusVal === 'no' && cncName !== '-' ? 'text-red-700 font-medium' : ''}`}>
                                                    {cncName}
                                                </td>
                                                <td className="p-1 border border-slate-200 text-slate-600 italic">
                                                    {justificationText}
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Footer Summary / Pagination */}
            <div className="px-3 py-1 bg-slate-100 border-t border-slate-200 text-[9px] text-slate-500 flex items-center justify-between shrink-0">
                <span>
                    Mostrando {displayedAllocations.length} de {filteredAllocations.length} partidas
                </span>
                {totalPages > 1 && (
                    <span>
                        Página {pageIndex + 1} de {totalPages}
                    </span>
                )}
            </div>
        </div>
    );
};
